import { Picture } from 'src/components/ui/Image'
import type {
  BannerMoodProps,
  Banner,
} from 'src/components/department/types/banner-mood'
import { Container } from 'src/components/department/Container'
import { InView } from 'react-intersection-observer'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'

import { useBannerMood } from './useBannerMood'
import './banner-mood.scss'

export const BannerMood = (data: BannerMoodProps) => {
  const { validateProps } = useBannerMood()

  const propsIsValid = validateProps(data)

  if (!propsIsValid) {
    return <></>
  }

  const { enableSession, sessionTitle, categories, section, locationOnPage } =
    data

  const sendPromoFeatureViewedEvent = (item: Banner) => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: item.Texto ?? '',
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  const standardObject = getStandardObject()

  if (!enableSession) {
    return <></>
  }

  const sendAmplitudeClickEvent = (banner: Banner, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: sessionTitle ?? banner?.Texto ?? '',
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
        'promotional element name': banner?.Texto ?? banner?.altImage ?? '',
        'promotional element index': index ?? 0,
      },
    })
  }

  return (
    <Container
      data-testid="banner-mood"
      role="region"
      aria-label="sessão de banners mood"
      className="container-banner-mood py-[40px] restructure-tablet:py-[48px] restructure-small-desktop:py-[60px] flex justify-center flex-col restructure-small-desktop:px-4"
    >
      <h2 className="desktop-heading-title3 mobile-heading-title3 text-restructure-primary mb-6 restructure-small-desktop:mb-8 pl-4 restructure-small-desktop:pl-0">
        {sessionTitle}
      </h2>

      <article className="w-full flex gap-[8px] restructure-tablet:gap-12 restructure-small-desktop:gap-24 overflow-auto scrollbar-hide px-4 restructure-small-desktop:px-0 ">
        {categories.map((banner, index) => {
          const color = banner.textColor === 'branco' ? '#FFFFFF' : '#15181B'

          return (
            <InView
              as="a"
              threshold={0.7}
              onChange={(inView) => {
                if (inView) {
                  sendPromoFeatureViewedEvent(banner)
                }
              }}
              triggerOnce
              key={banner.linkURL}
              href={banner.linkURL}
              className="rounded-lg overflow-hidden relative w-maxcursor-pointer min-w-[177px] restructure-tablet:min-w-[290px] restructure-small-desktop:min-w-0 restructure-small-desktop:flex-1"
              onClick={() => sendAmplitudeClickEvent(banner, index)}
            >
              <Picture
                sources={[
                  {
                    src: banner.image.desktop,
                    width: 302,
                    height: 302,
                    media: '(min-width: 600px)',
                  },
                ]}
                img={{
                  src: banner.image.mobile,
                  width: 177,
                  height: 177,
                  alt: banner.altImage,
                  className:
                    'w-full transition-transform duration-500 ease-in-out hover:scale-110',
                  loading: 'lazy',
                }}
              />

              <div className="flex absolute bottom-0 left-0 justify-between w-full p-3 restructure-tablet:p-[20px] restructure-small-desktop:p-6 items-end">
                <h3
                  className={`flex-1 desktop-heading-title4 tablet-heading-title4 mobile-heading-title5 text-restructure-primary ${
                    banner.textColor && banner.textColor === 'branco'
                      ? 'text-restructure-primary-inverted'
                      : 'text-restructure-primary'
                  }`}
                >
                  {banner.Texto}
                </h3>
                <button
                  title={`Botão ${banner.Texto}`}
                  type="button"
                  className="h-[32px] w-[32px] rounded-round desktop-body-semibold-text2 items-center justify-center gap-xs hidden restructure-small-desktop:flex hover:bg-opacity-5 hover:bg-restructure-background-primary border-sm active:bg-opacity-10 border-restructure-border-primary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill={color}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.25 12C4.25 11.5858 4.58579 11.25 5 11.25H19C19.4142 11.25 19.75 11.5858 19.75 12C19.75 12.4142 19.4142 12.75 19 12.75H5C4.58579 12.75 4.25 12.4142 4.25 12Z"
                      fill={color}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.4697 4.46967C11.7626 4.17678 12.2374 4.17678 12.5303 4.46967L19.5303 11.4697C19.8232 11.7626 19.8232 12.2374 19.5303 12.5303L12.5303 19.5303C12.2374 19.8232 11.7626 19.8232 11.4697 19.5303C11.1768 19.2374 11.1768 18.7626 11.4697 18.4697L17.9393 12L11.4697 5.53033C11.1768 5.23744 11.1768 4.76256 11.4697 4.46967Z"
                      fill={color}
                    />
                  </svg>
                </button>
              </div>
            </InView>
          )
        })}
      </article>
    </Container>
  )
}
